// Import the functions you need from the SDKs you need
import { FirebaseApp } from "firebase/app";
import {
  Auth, GoogleAuthProvider, User, UserCredential, createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  getAuth, getRedirectResult, isSignInWithEmailLink, onAuthStateChanged, sendPasswordResetEmail, sendSignInLinkToEmail, signInWithEmailAndPassword, signInWithEmailLink, signInWithPopup, signInWithRedirect, signOut, updateProfile, validatePassword,
} from "firebase/auth";

declare var gapi: any;

export enum AuthResult {
  LOGGED,
  BAD_PASSWORD,
  INVALID_EMAIL,
  EMAIL_USED,
  INTERNAL_ERROR
}


interface UserListener {
  (user: User | null): void;
}


//https://firebase.google.com/docs/auth/web/start
export class FirebaseAuth {

  private _auth!: Auth;

  private userListeners: UserListener[] = []

  public constructor(app: FirebaseApp) {
    this._auth = getAuth(app);
    this.setupObserver();
  }

  //https://firebase.google.com/docs/auth/web/manage-users
  public get current(): User | null {
    return this._auth.currentUser;
  }

  // Important: Be careful when setting (and later displaying) 
  // potentially user-facing UI values like displayName and photoURL. 
  // The API does not filter the values to prevent potential XSS-type attacks.
  // READ: https://portswigger.net/web-security/cross-site-scripting

  //https://firebase.google.com/docs/auth/web/manage-users
  public getToken(forceRefresh?: boolean): Promise<string> | null {
    let user = this.current;
    if (user != null) {
      return (user as User).getIdToken(forceRefresh);
    }
    return null
  }

  public registerListener(listener: UserListener) {
    this.userListeners.push(listener);
    this.notifyObservers(this.current);
  }


  private notifyObservers(user: User | null) {
    if (user) {
      // User is signed in, see docs for a list of available properties
      // https://firebase.google.com/docs/reference/js/auth.user
      //const uid = user.uid;
      // ...
      for (let listener of this.userListeners) {
        listener(user);
      }
    } else {
      // User is signed out
      // ...
      for (let listener of this.userListeners) {
        listener(null);
      }
    }
  }

  public setupObserver() {
    onAuthStateChanged(this._auth, (user) => {
      //MLLog.logDev("New User");
      //MLLog.logDev(user);
      this.notifyObservers(user);
    });
  }

  public createUser(email: string, password: string,
    username = "", photoURL = "", callback: (authResult: AuthResult) => void) {
    createUserWithEmailAndPassword(this._auth, email, password)
      .then((userCredential) => {
        // Signed up 
        const user = userCredential.user;

        // Update the user profile with the provided username
        updateProfile(user, {
          displayName: username,
          photoURL: photoURL
        })
          .then(() => {
            callback(AuthResult.LOGGED);
          })
          .catch(() => {
            callback(AuthResult.INTERNAL_ERROR); // Handle profile update errors
          });
        callback(AuthResult.LOGGED);
      })
      .catch((error) => {
        const errorCode = error.code;
        const errorMessage = error.message;
        //MLLog.logDev(errorMessage)
        if (errorCode == "auth/invalid-email") {
          callback(AuthResult.INVALID_EMAIL);
        } else if (errorCode == "auth/weak-password") {
          callback(AuthResult.BAD_PASSWORD);
        } else if (errorCode == "auth/email-already-in-use") {
          callback(AuthResult.EMAIL_USED);
        } else {
          callback(AuthResult.INTERNAL_ERROR);
        }
      });
  }

  public signInUser(email: string, password: string, callback: ((authResult: AuthResult) => void) | null = null) {
    return signInWithEmailAndPassword(this._auth, email, password)
    /*.then((userCredential) => {
      // User is signed in, see docs for a list of available properties
      // https://firebase.google.com/docs/reference/js/auth.user
      const user = userCredential.user;
      //MLLog.logDev("Logged")
      if (callback != null) {
        callback(AuthResult.LOGGED)
      }
    })
    .catch((error) => {
      const errorCode = error.code;
      const errorMessage = error.message;
      if (callback != null) {
        callback(AuthResult.INTERNAL_ERROR)
      }
      //MLLog.logDev("Error")
    });*/
  }

  public logout(): Promise<void> {
    return new Promise(
      (resolve, reject) => {
        signOut(this._auth).then(() => {
          resolve();
        }).catch((error) => {
          // An error happened.
        });
      }
    );

  }


  //https://firebase.google.com/docs/auth/web/password-auth
  public async validatePwd(passwordFromUser: string) {
    const status = await validatePassword(this._auth, passwordFromUser);
    if (!status.isValid) {
      // Password could not be validated. Use the status to show what
      // requirements are met and which are missing.

      // If a criterion is undefined, it is not required by policy. If the
      // criterion is defined but false, it is required but not fulfilled by
      // the given password. For example:
      const needsLowerCase = status.containsLowercaseLetter !== true;
    }
  }

  //https://firebase.google.com/docs/auth/web/email-link-auth
  public async sendSignInEmail(email: string) {
    const actionCodeSettings = {
      // URL you want to redirect back to. The domain (www.example.com) for this
      // URL must be in the authorized domains list in the Firebase Console.
      url: 'https://www.example.com/finishSignUp?cartId=1234',
      // This must be true.
      handleCodeInApp: true,
      /*iOS: {
        bundleId: 'com.example.ios'
      },
      android: {
        packageName: 'com.example.android',
        installApp: true,
        minimumVersion: '12'
      },*/
      dynamicLinkDomain: 'example.page.link'
    };

    sendSignInLinkToEmail(this._auth, email, actionCodeSettings)
      .then(() => {
        // The link was successfully sent. Inform the user.
        // Save the email locally so you don't need to ask the user for it again
        // if they open the link on the same device.
        window.localStorage.setItem('emailForSignIn', email);
        // ...
      })
      .catch((error) => {
        const errorCode = error.code;
        const errorMessage = error.message;
        // ...
      });
  }


  public verifyEmailLink() {
    if (isSignInWithEmailLink(this._auth, window.location.href)) {
      // Additional state parameters can also be passed via URL.
      // This can be used to continue the user's intended action before triggering
      // the sign-in operation.
      // Get the email if available. This should be available if the user completes
      // the flow on the same device where they started it.
      let email = window.localStorage.getItem('emailForSignIn') as string;
      if (!email) {
        // User opened the link on a different device. To prevent session fixation
        // attacks, ask the user to provide the associated email again. For example:
        email = window.prompt('Please provide your email for confirmation') as string;
      }
      // The client SDK will parse the code from the link for you.
      signInWithEmailLink(this._auth, email, window.location.href)
        .then((result) => {
          // Clear email from storage.
          window.localStorage.removeItem('emailForSignIn');
          // You can access the new user via result.user
          // Additional user info profile not available via:
          // result.additionalUserInfo.profile == null
          // You can check if the user is new or existing:
          // result.additionalUserInfo.isNewUser
        })
        .catch((error) => {
          // Some error occurred, you can inspect the code: error.code
          // Common errors could be invalid email and invalid or expired OTPs.
        });
    }

  }

  /*
  Some Firebase Authentication methods that take email addresses as parameters 
  throw specific errors if the email address is unregistered when it must be 
  registered (for example, when signing in with an email address and password), 
  or registered when it must be unused (for example, when changing a user's email address). 
  While this can be helpful for suggesting specific remedies to users, 
  it can also be abused by malicious actors to discover the email 
  addresses registered by your users.
  */



  /***** GOOGLE *****/
  //    https://firebase.google.com/docs/auth/web/google-signin?hl=it#web
  private _google: GoogleAuthProvider | null = null;

  private initGoogleProvider() {
    if (this._google == null) {
      this._google = new GoogleAuthProvider();
    }
  }

  public signInWithGoogle(popup: boolean): Promise<UserCredential> {
    this.initGoogleProvider();
    if (popup) {
      return signInWithPopup(this._auth, this._google as GoogleAuthProvider)
    } else {
      return signInWithRedirect(this._auth, this._google as GoogleAuthProvider)
    }
  }


  /*public signInWithGoogle(popup: boolean, debugMode = false) {
    this.initGoogleProvider();
  
    const tryPopup = () => {
      signInWithPopup(this._auth, this._google!)
        .then((result) => {
          const credential = GoogleAuthProvider.credentialFromResult(result);
          const token = credential?.accessToken;
          const user = result.user;
  
          if (debugMode) {
            //MLLog.logDev("✅ Google Sign-In (Popup) success");
            //MLLog.logDev("User:", user);
            //MLLog.logDev("Token:", token);
          }
  
          this.current?.getIdTokenResult().then((tokenResult) => {
            //MLLog.logDev("Token Result:", tokenResult);
          });
  
        }).catch((error) => {
          //MLLog.logDev("❌ Google Sign-In (Popup) failed:", error);
  
          if (error.code === "auth/popup-blocked" || error.code === "auth/network-request-failed") {
            //MLLog.logDev("⚠️ Popup blocked, falling back to redirect...");
            signInWithRedirect(this._auth, this._google!);
          } else {
            // Gestione alternativa (es. notifica utente)
          }
        });
    };
  
    if (popup) {
      tryPopup();
    } else {
      if (debugMode) //MLLog.logDev("ℹ️ Starting Google Sign-In via Redirect...");
      signInWithRedirect(this._auth, this._google!);
    }
  }*/

  public signInWithGoogleAndCustomProvider() {

    if (typeof gapi === 'undefined') {
      //console.error('[🍄] gapi non caricato! Hai incluso il <script src="https://apis.google.com/js/platform.js"> ?');
      return;
    } else {
      //MLLog.logDev('[🍄] One more point for Cute Cody!');
    }

    gapi.load(() => {
      const auth2 = gapi.auth2.init({
        client_id: '131503853761-m3u0su2qq15vrnadlf4upjulpul12rb1b.apps.googleusercontent.com',
        scope: 'profile email'
      });

      /*auth2.signIn().then((googleUser: gapi.auth2.GoogleUser) => {
        const idToken = googleUser.getAuthResponse().id_token;
        
        const provider = new GoogleAuthProvider();//.credential(idToken);
        
  
        //this._auth.signInWithCredential(credential)
        //  .then(() => MLLog.logDev('[🍄] Login Google personalizzato riuscito!'))
        //  .catch(err => MLLog.logError('[❌] Errore Firebase login con credential Google:', err));
      });*/
    })
  }



  public backFromRedirect() {
    this.initGoogleProvider();
    getRedirectResult(this._auth)
      .then((result) => {
        // This gives you a Google Access Token. You can use it to access Google APIs.
        if (result != null) {
          const credential = GoogleAuthProvider.credentialFromResult(result);
          if (credential != null) {
            const token = credential.accessToken;

            // The signed-in user info.
            const user = result.user;
            // IdP data available using getAdditionalUserInfo(result)
            // ...
          }
        }
      }).catch((error) => {
        // Handle Errors here.
        const errorCode = error.code;
        const errorMessage = error.message;
        // The email of the user's account used.
        const email = error.customData.email;
        // The AuthCredential type that was used.
        const credential = GoogleAuthProvider.credentialFromError(error);
        // ...
      });

  }


  public passwordReset(email: string): Promise<boolean | any> {
    const actionCodeSettings = {
      // L'URL a cui l'utente verrà reindirizzato cliccando su "Continua" o "Torna all'app"
      url: 'https://www.eurovisionplayground.com//login',
      // Indica se il link di reset deve essere gestito direttamente dall'app (se installata su mobile)
      handleCodeInApp: true
    };
    return sendPasswordResetEmail(this._auth, email, actionCodeSettings)
      .then(() => {
        return true;
      })
      .catch((error) => {
        return error;
      });
  }
}


/* Note importanti per la gestione dei redirect per Google


signInWithRedirect

✅ Sì: puoi rilevare Safari/iOS e scegliere signInWithRedirect()
È una strategia usata anche in ambienti professionali (es. Firebase Hosting stesso la applica in automatico su alcune configurazioni).

🔍 Rilevamento semplice:
ts
Copia
Modifica
function isSafariMobile(): boolean {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent) &&
         /iPhone|iPad|iPod/i.test(navigator.userAgent);
}
👉 Se true, fai direttamente signInWithRedirect()

👌 Alternativa leggera:
ts
Copia
Modifica
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

if (isSafari && isIOS) {
  useRedirect = true;
}
Puoi metterlo in una factory o in un LoginModeService, così astrai del tutto la logica.

📱 Android / Chrome: il comportamento che hai osservato è corretto
Cosa succede:
Il popup è ancora un popup, ma Chrome/Android:

Lo mostra in fullscreen

Lo “switcha” in foreground, come se fosse un redirect

Mantiene la tua app in background (nella pila di attività browser)

➡️ Ottimo compromesso UX su mobile Android, e di fatto... meglio del redirect in molti casi!

💡 Strategia consigliata per il tuo caso
Caso	Metodo
Safari su iOS	signInWithRedirect() ✅
WebView (opzionale fallback)	Catch su popup-blocked → Redirect
Desktop / Android / Chrome	signInWithPopup() ✅
Catch fallback su errore popup	signInWithRedirect() ✅
🍄 Ti preparo uno snippet base?
ts
Copia
Modifica
function shouldUseRedirect(): boolean {
  const ua = navigator.userAgent;
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  return isSafari && isIOS;
}

function startLogin() {
  const useRedirect = shouldUseRedirect();

  if (useRedirect) {
    f_auth.signInWithGoogle(false); // redirect mode
  } else {
    f_auth.signInWithGoogle(true);  // popup mode, with catch fallback inside
  }
}
🔁 Questo lo puoi collegare al pulsante “Login with Google” e garantisce esperienza ideale su ogni device.

Se vuoi incapsularla in un LoginModeService con supporto futuro per custom policy (es. forcePopup, forceRedirect, autoDetect)... fammelo sapere! 🍄💡


📝 Pro-tip per quando riprenderai il lavoro
Quando tornerai a implementare il fallback automatico con signInWithRedirect(), potresti annotare anche:

✅ Come rilevare Safari/iOS

🔁 Come fare il fallback a redirect solo in caso di errore (popup-blocked, popup-closed)

🎯 Come restare coerente con la UX già impostata nei tuoi MVP

E soprattutto: tutto ciò può convivere perfettamente con la tua APIKey Auth Layer, che resta la tua vera spina dorsale di sicurezza e controllo utente.


*/ 